import { useMemo } from 'react';
import { usePersistedJSON } from 'src/hooks/usePersistedJSON';

import { DEFAULT_DISPLAY_METRICS, DisplayMetric, effectiveDisplayMetrics, isDisplayMetricArray } from './displayMetricKeys';

/**
 * 表示項目の localStorage キー。/map と convenience が同じ設定を共有する。
 * 変えると既存ユーザーの保存値が読めなくなるので変更しない。
 */
export const DISPLAY_METRICS_STORAGE_KEY = 'convenienceDispatch.displayMetrics';

/**
 * 表示項目の永続化 + 実効 Set の導出。キー・既定値・バリデータの 3 点セットに加え、
 * effectiveDisplayMetrics を通した Set (第3要素) までここで返す。
 *
 * 実効 Set まで hook が返す理由: 各 Presenter が effectiveDisplayMetrics(displayMetrics)
 * を自分で呼んで Set を組む形だと、呼び忘れの余地が残る。実際に Map/ConvenienceDispatches
 * の両 Presenter で呼び忘れが起き、保存値が数値指標ゼロ (粗利だけ等) のとき未割当カードの
 * 数量だけ出ない不具合が2回発生した。「hook が返したものを使うだけ」にすることで、
 * Presenter 側に導出を書く余地そのものを無くす。
 *
 * 第1要素（生の保存値）と第2要素（setter）は変更前と同じもの。DisplayMetricToggle には
 * 引き続き生の値を渡す。実効配列を渡すとトグル操作で quantity が保存値に書き込まれ、
 * 「保存値は書き換えない」という非破壊方針が壊れるため。
 *
 * useMemo で包む理由: 包まないと毎レンダーで新しい Set 参照ができ、displayMetrics が
 * 変わっていなくても memo 化された下流コンポーネント (ConvenienceDispatchVehicleList 等)
 * の再レンダー抑止が効かなくなる。
 */
export function useDisplayMetrics(): readonly [
  DisplayMetric[],
  (updater: DisplayMetric[] | ((prev: DisplayMetric[]) => DisplayMetric[])) => void,
  Set<DisplayMetric>,
] {
  const [displayMetrics, setDisplayMetrics] = usePersistedJSON<DisplayMetric[]>(
    DISPLAY_METRICS_STORAGE_KEY,
    DEFAULT_DISPLAY_METRICS,
    isDisplayMetricArray,
  );
  const displayMetricSet = useMemo(
    () => new Set(effectiveDisplayMetrics(displayMetrics)),
    [displayMetrics],
  );
  return [displayMetrics, setDisplayMetrics, displayMetricSet] as const;
}
